import React, { useState, useEffect, memo } from 'react';
import { Menu, X, Edit3 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { trackCtaClick } from '@/lib/ga4Tracking.js';
import { handleWhatsAppClick } from '@/lib/whatsapp.js';
import { useEditContext } from '@/contexts/EditContext.jsx';
import logoHiperpan from '@/assets/logo-hiperpan.png';

const Header = memo(() => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAdmin, editMode, toggleEditMode } = useEditContext();

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({ top: element.offsetTop - 80, behavior: 'smooth' });
    }
  };

  const handleCtaClick = () => {
    trackCtaClick('Solicitar Atendimento', 'header', 'button');
    handleWhatsAppClick('Olá! Gostaria de solicitar um atendimento comercial para conhecer melhor os produtos da Hiperpan.');
  };

  return (
    <header 
      className={`fixed top-0 left-0 w-full z-50 transition-colors duration-300 border-b py-3 md:py-4 max-w-[100vw] ${
        isScrolled ? 'bg-white shadow-md border-transparent' : 'bg-white border-border'
      }`}
    >
      <div className="container-custom flex items-center justify-between gap-4">
        
        {/* Logo */}
        <button 
          className="flex-shrink-0 cursor-pointer min-h-[44px] flex items-center" 
          onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
          aria-label="Ir para o topo da página"
        >
          <img
            src={logoHiperpan}
            alt="Logotipo oficial da Hiperpan"
            width="320"
            height="200"
            loading="eager"
            className="h-8 sm:h-10 lg:h-12 w-auto object-contain"
          />
        </button>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-8" aria-label="Navegação principal">
          <button onClick={() => scrollTo('hero')} className="text-foreground hover:text-primary font-bold text-sm uppercase tracking-wider transition-colors min-h-[44px] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-primary whitespace-nowrap">Início</button>
          <button onClick={() => scrollTo('institucional')} className="text-foreground hover:text-primary font-bold text-sm uppercase tracking-wider transition-colors min-h-[44px] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-primary whitespace-nowrap">Diferenciais</button>
          <button onClick={() => scrollTo('confianca')} className="text-foreground hover:text-primary font-bold text-sm uppercase tracking-wider transition-colors min-h-[44px] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-primary whitespace-nowrap">Sobre</button>
        </nav>

        {/* Desktop CTA & Mobile Toggle */}
        <div className="flex items-center gap-3 lg:gap-4 flex-shrink-0">
          {/* Admin Toggle */}
          {isAdmin && (
            <button
              onClick={toggleEditMode}
              className={`hidden lg:flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-colors duration-300 border min-h-[44px] whitespace-nowrap ${
                editMode 
                  ? 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200' 
                  : 'bg-muted text-foreground border-border hover:bg-muted/80'
              }`}
              aria-label={editMode ? 'Sair do modo de edição' : 'Entrar no modo de edição'}
            >
              <Edit3 className="w-4 h-4 flex-shrink-0" />
              <span>{editMode ? 'Sair Edição' : 'Editar Conteúdo'}</span>
            </button>
          )}

          <button
            id="btn-header-atendimento"
            data-gtm="whatsapp"
            onClick={handleCtaClick}
            className="gtm-whatsapp hidden lg:inline-flex bg-primary hover:bg-primary/90 text-primary-foreground font-body font-bold rounded-xl items-center justify-center transition-colors duration-200 active:scale-[0.98] min-h-[44px] px-4 xl:px-6 text-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary whitespace-nowrap shadow-[0_10px_25px_-6px_rgba(0,0,0,0.25),0_0_24px_-4px_hsla(160,84%,39%,0.35)]"
            aria-label="Solicitar Atendimento Comercial"
          >
            Solicitar Atendimento
          </button>
          
          <button 
            className="lg:hidden text-foreground p-2 bg-muted rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary flex-shrink-0"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            id="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden absolute top-full left-0 w-full max-w-[100vw] bg-white border-t border-border shadow-xl overflow-hidden"
          >
            <nav className="p-4 sm:p-6 flex flex-col gap-1 container-custom" aria-label="Navegação mobile">
              <button onClick={() => scrollTo('hero')} className="text-left text-foreground font-bold text-base sm:text-lg py-3 sm:py-4 border-b border-muted min-h-[44px] break-words">Início</button>
              <button onClick={() => scrollTo('institucional')} className="text-left text-foreground font-bold text-base sm:text-lg py-3 sm:py-4 border-b border-muted min-h-[44px] break-words">Diferenciais</button>
              <button onClick={() => scrollTo('confianca')} className="text-left text-foreground font-bold text-base sm:text-lg py-3 sm:py-4 border-b border-muted min-h-[44px] break-words">Sobre</button>
              
              {isAdmin && (
                <button 
                  onClick={() => { toggleEditMode(); setMobileMenuOpen(false); }}
                  className={`text-left font-bold text-base sm:text-lg py-3 sm:py-4 border-b border-muted flex items-center gap-2 min-h-[44px] break-words ${
                    editMode ? 'text-amber-600' : 'text-foreground'
                  }`}
                >
                  <Edit3 className="w-5 h-5 flex-shrink-0" />
                  <span>{editMode ? 'Sair Edição' : 'Editar Conteúdo'}</span>
                </button>
              )}

              <div className="pt-4 sm:pt-6 pb-2">
                <button
                  id="btn-header-atendimento-mobile"
                  data-gtm="whatsapp"
                  onClick={handleCtaClick}
                  className="gtm-whatsapp w-full bg-primary hover:bg-primary/90 text-primary-foreground font-body font-bold rounded-xl flex items-center justify-center transition-colors duration-200 min-h-[48px] py-3 px-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary break-words text-center leading-tight shadow-[0_10px_25px_-6px_rgba(0,0,0,0.25),0_0_24px_-4px_hsla(160,84%,39%,0.35)]"
                >
                  Solicitar Atendimento
                </button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
});

Header.displayName = 'Header';
export default Header;